import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { EligibleListDTO, EventDetailDTO } from "../../../../models/EventDTO";
import S from "./strings";
import { Opciones, errorText, normalizar } from "../workshops/comun";
import { fechaBonita } from "../../../../utils/fechaBonita";
import useEsTelefono from "../../../../utils/esTelefono";

interface Props { campusId: number; evento: EventDetailDTO; opciones: Opciones; reload: () => void; notify: (t: string, ok?: boolean) => void; }

type Filtro = "todas" | "listas" | "reponer";
const TOPE = 300;

/** Elegibles: horario que termina cerca del evento. "Diploma listo" o "N por reponer"; se agregan en lote. */
const ElegiblesTab: React.FC<Props> = ({ campusId, evento, opciones, reload, notify }) => {
	const http = HttpManager.getInstance();
	const [cursoId, setCursoId] = useState<number>(0);
	const [data, setData] = useState<EligibleListDTO | null>(null);
	const [filtro, setFiltro] = useState<Filtro>("todas");
	const [q, setQ] = useState<string>("");
	const [sel, setSel] = useState<number[]>([]);
	const [cobra, setCobra] = useState<boolean>((evento.packagePrice || 0) > 0);
	const [busy, setBusy] = useState<boolean>(false);
	const [tick, setTick] = useState<number>(0);
	const movil = useEsTelefono();

	useEffect(() => {
		let vigente = true;
		setData(null); setSel([]);
		http.eventsService.eligible(campusId, evento.id, cursoId || undefined)
			.then(r => vigente && setData(r.data))
			.catch(e => { if (vigente) { setData({ eventId: evento.id, rows: [], counts: {} }); notify(errorText(e), false); } });
		return () => { vigente = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [campusId, evento.id, cursoId, tick]);

	const filas = useMemo(() => {
		const t = normalizar(q.trim());
		return (data?.rows || []).filter(r => (filtro === "todas" || (filtro === "listas" ? r.diplomaReady : !r.diplomaReady))
			&& (!t || normalizar([r.studentName, r.identityCode, r.courseName].join(" ")).includes(t)));
	}, [data, filtro, q]);
	const visibles = filas.slice(0, TOPE);
	const libres = visibles.filter(r => !r.alreadyAdded).map(r => r.enrollmentId);
	const todas = libres.length > 0 && libres.every(id => sel.includes(id));

	const agregar = () => {
		setBusy(true);
		http.eventsService.addEligible(campusId, evento.id, sel, cobra)
			.then(r => { notify(S.agregadas(r.data.added, (r.data.skipped || []).length), (r.data.skipped || []).length === 0); setTick(x => x + 1); reload(); })
			.catch(e => notify(errorText(e), false))
			.finally(() => setBusy(false));
	};
	const chips: { k: Filtro; t: string; n?: number }[] = [
		{ k: "todas", t: S.elegTodas, n: data?.counts?.eligible }, { k: "listas", t: S.elegListas, n: data?.counts?.diplomaReady }, { k: "reponer", t: S.elegReponer, n: data?.counts?.toRecover }
	];

	return (
		<div>
			<div className="wau-chips" role="group">
				{chips.map(c => <button key={c.k} type="button" className="wau-chip" aria-pressed={filtro === c.k} onClick={() => setFiltro(c.k)}>{c.t}{c.n != null ? <span className="wau-num"> ({c.n})</span> : null}</button>)}
				<select className="wau-entrada" style={{ width: 240, marginLeft: "auto" }} aria-label={S.colCurso} value={cursoId} onChange={e => setCursoId(Number(e.target.value) || 0)}>
					<option value={0}>{S.cursoTodos}</option>
					{opciones.cursos.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
				</select>
				<input type="search" className="wau-entrada" style={{ width: 220 }} placeholder={S.buscarElegible} value={q} onChange={e => setQ(e.target.value)} />
			</div>
			{sel.length > 0 ? (
				<div className="wau-lote">
					<b>{S.agregarN(sel.length)}</b>
					<label style={{ display: "inline-flex", gap: 6, alignItems: "center", margin: 0, position: "static" }}><input type="checkbox" checked={cobra} onChange={e => setCobra(e.target.checked)} /><span>{S.cobraPaquete}</span></label>
					<button type="button" className="wau-btn p" disabled={busy} onClick={agregar}>{S.agregarBtn}</button>
				</div>
			) : null}
			{data === null ? <div className="wau-caja wau-sec">{S.cargando}</div> : movil ? (
				/* R2: en teléfono la tabla de seis columnas se partía letra por letra; cada alumna pasa a una tarjeta con su casilla a la vista */
				<div className="wau-caja">
					{filas.length > TOPE ? <div className="wau-sec wau-num" style={{ marginBottom: 6 }}>{S.mostrando(TOPE, filas.length)}</div> : null}
					<label style={{ display: "flex", gap: 8, alignItems: "center", margin: "0 0 8px 0", position: "static" }}>
						<input type="checkbox" checked={todas} disabled={libres.length === 0} onChange={() => setSel(todas ? [] : libres)} /><span>{S.elegTodas}</span></label>
					{visibles.length === 0 ? <p className="wau-sec" style={{ margin: 0 }}>—</p> : visibles.map(r => (
						<div key={r.enrollmentId} className="wau-tarjeta" style={sel.includes(r.enrollmentId) ? { borderColor: "var(--wa-acento)", background: "var(--wa-acento-suave)" } : undefined}>
							<label style={{ display: "flex", gap: 8, alignItems: "center", margin: 0, position: "static" }}>
								<input type="checkbox" disabled={r.alreadyAdded} checked={r.alreadyAdded || sel.includes(r.enrollmentId)}
									onChange={() => setSel(s => s.includes(r.enrollmentId) ? s.filter(x => x !== r.enrollmentId) : [...s, r.enrollmentId])} />
								<b>{r.studentName}</b></label>
							<div className="linea">{r.identityCode}{r.courseName ? " · " + r.courseName : ""}</div>
							<div className="linea wau-num">{S.colTermina} {fechaBonita(r.scheduleEndDate)}{r.attendancePct != null ? " · " + S.colAsistencia + " " + r.attendancePct + " %" : ""}</div>
							<div style={{ marginTop: 6 }}>{r.alreadyAdded ? <span className="wau-pastilla">{S.yaAgregada}</span>
								: r.diplomaReady ? <span className="wau-pastilla acento">{S.elegListas}</span>
									: <span className="wau-pastilla oro">{S.nPorReponer(r.sessionsToRecover)}</span>}</div>
						</div>
					))}
				</div>
			) : (
				<div className="wau-caja wau-tabla">
					{filas.length > TOPE ? <div className="wau-sec wau-num" style={{ marginBottom: 6 }}>{S.mostrando(TOPE, filas.length)}</div> : null}
					<table>
						<thead><tr>
							<th><input type="checkbox" checked={todas} disabled={libres.length === 0} aria-label={S.elegTodas} onChange={() => setSel(todas ? [] : libres)} /></th>
							<th>{S.colNombre}</th><th>{S.colCurso}</th><th>{S.colTermina}</th><th className="der">{S.colAsistencia}</th><th>{S.colEstado}</th>
						</tr></thead>
						<tbody>
							{visibles.length === 0 ? <tr><td colSpan={6} className="wau-sec" style={{ textAlign: "center", padding: 18 }}>—</td></tr> : visibles.map(r => (
								<tr key={r.enrollmentId} className={sel.includes(r.enrollmentId) ? "sel" : ""}>
									<td><input type="checkbox" disabled={r.alreadyAdded} checked={r.alreadyAdded || sel.includes(r.enrollmentId)} aria-label={r.studentName}
										onChange={() => setSel(s => s.includes(r.enrollmentId) ? s.filter(x => x !== r.enrollmentId) : [...s, r.enrollmentId])} /></td>
									<td><b>{r.studentName}</b><div className="wau-sec" style={{ fontSize: 12.5 }}>{r.identityCode}</div></td>
									<td style={{ fontSize: 13.5 }}>{r.courseName}<div className="wau-sec" style={{ fontSize: 12.5 }}>{r.scheduleName}</div></td>
									<td className="wau-num" style={{ whiteSpace: "nowrap" }}>{fechaBonita(r.scheduleEndDate)}</td>
									<td className={"der wau-num" + ((r.attendancePct ?? 0) < r.minAttendancePct ? " wau-oro" : "")}>{r.attendancePct != null ? r.attendancePct + " %" : "—"}</td>
									<td>{r.alreadyAdded ? <span className="wau-pastilla">{S.yaAgregada}</span>
										: r.diplomaReady ? <span className="wau-pastilla acento">{S.elegListas}</span>
											: <span className="wau-pastilla oro">{S.nPorReponer(r.sessionsToRecover)}</span>}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};

export default ElegiblesTab;
